import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Pencil, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";

export default function Products() {
  const { apiCall } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", price: "", cost: "", stock_quantity: "", min_stock: "", supplier_id: "", category: "", commission_rate: "" });
  const [suppliers, setSuppliers] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, sRes] = await Promise.all([
        apiCall("get", "/products"),
        apiCall("get", "/suppliers"),
      ]);
      setProducts(pRes.data);
      setSuppliers(sRes.data);
    } catch {
      toast.error("Erro ao carregar produtos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleOpenNew = () => {
    setEditing(null);
    setForm({ name: "", description: "", price: "", cost: "", stock_quantity: "", min_stock: "", supplier_id: "", category: "", commission_rate: "" });
    setOpen(true);
  };

  const handleEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      description: p.description || "",
      price: p.price.toString(),
      cost: (p.cost || 0).toString(),
      stock_quantity: p.stock_quantity.toString(),
      min_stock: (p.min_stock || 0).toString(),
      supplier_id: p.supplier_id || "",
      category: p.category || "",
      commission_rate: (p.commission_rate ?? "").toString(),
    });
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        cost: parseFloat(form.cost) || 0,
        stock_quantity: parseInt(form.stock_quantity),
        min_stock: parseInt(form.min_stock) || 0,
        supplier_id: form.supplier_id || null,
        commission_rate: parseFloat(form.commission_rate) || 0,
      };
      if (editing) {
        await apiCall("put", `/products/${editing.product_id}`, payload);
        toast.success("Produto atualizado");
      } else {
        await apiCall("post", "/products", payload);
        toast.success("Produto criado");
      }
      setOpen(false);
      load();
    } catch {
      toast.error("Erro ao salvar");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Excluir este produto?")) return;
    try {
      await apiCall("delete", `/products/${id}`);
      toast.success("Produto excluído");
      load();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

  return (
    <div className="space-y-8" data-testid="products-page">
      <PageHeader overline="Cadastros" title="Produtos" subtitle="Gerencie o catálogo de produtos">
        <Button onClick={handleOpenNew} data-testid="add-product-button">
          <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Novo Produto
        </Button>
      </PageHeader>

      <Card className="border border-border bg-card shadow-soft rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/30 hover:bg-secondary/30">
              <TableHead className="text-xs uppercase tracking-widest font-medium">Nome</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Categoria</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Estoque</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Preço</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Custo</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Margem</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest font-medium">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
            ) : products.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">Nenhum produto cadastrado.</TableCell></TableRow>
            ) : (
              products.map((p) => (
                <TableRow key={p.product_id} data-testid={`product-row-${p.product_id}`}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{p.category || "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className={p.stock_quantity < 5 ? "text-destructive font-medium" : ""}>{p.stock_quantity}</span>
                      {p.stock_quantity < 5 && <AlertCircle className="h-3 w-3 text-destructive" strokeWidth={1.5} />}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium" style={{ color: "#CBA871" }}>{formatCurrency(p.price)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatCurrency(p.cost || 0)}</TableCell>
                  <TableCell className="text-sm font-medium" style={{ color: (p.price - (p.cost || 0)) >= 0 ? "#166534" : "#991B1B" }}>
                    {formatCurrency((p.price || 0) - (p.cost || 0))}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(p)} data-testid={`edit-product-${p.product_id}`}>
                      <Pencil className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(p.product_id)} data-testid={`delete-product-${p.product_id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" strokeWidth={1.5} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">{editing ? "Editar Produto" : "Novo Produto"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="product-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Categoria</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Ex: Cabelo, Unhas, Skincare" data-testid="product-category-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Descrição</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="product-description-input" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest">Preço (R$)</Label>
                <Input type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required data-testid="product-price-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Custo (R$)</Label>
                <Input type="number" step="0.01" min="0" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} placeholder="0,00" data-testid="product-cost-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Estoque</Label>
                <Input type="number" min="0" value={form.stock_quantity} onChange={(e) => setForm({ ...form, stock_quantity: e.target.value })} required data-testid="product-stock-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest">Estoque mínimo (alerta)</Label>
                <Input type="number" min="0" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} placeholder="Ex: 3" data-testid="product-min-stock-input" />
                <p className="text-[10px] text-muted-foreground mt-1">Aparecerá na Lista de Compras quando estoque ≤ esse valor.</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Fornecedor</Label>
                <select
                  className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                  value={form.supplier_id}
                  onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}
                  data-testid="product-supplier-select"
                >
                  <option value="">— Nenhum —</option>
                  {suppliers.map((s) => (
                    <option key={s.supplier_id} value={s.supplier_id}>{s.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Comissão na venda (%)</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={form.commission_rate}
                onChange={(e) => setForm({ ...form, commission_rate: e.target.value })}
                placeholder="0 = sem comissão"
                data-testid="product-commission-input"
              />
              <p className="text-[10px] text-muted-foreground mt-1">% paga ao profissional quando este produto for vendido no fechamento da comanda. Deixe 0 para venda sem comissão.</p>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="product-save-button">{editing ? "Atualizar" : "Criar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
